import React from "react";
import PropTypes from "prop-types";
import { 
  FaTimes, FaUser, FaPhone, FaCut, FaClock, 
  FaSyringe, FaTag, FaNotesMedical, FaHeartbeat,
  FaUtensils, FaCheck
} from "react-icons/fa";
import { getPetIcon } from "../../components/common/PetDetailsBadge";
import { useProfile } from "../../context/ProfileContext";

const COLOR_HEX_MAP = {
  white: "#FFFFFF",
  black: "#1F2937",
  brown: "#795548",
  golden: "#F4B400",
  gold: "#F4B400",
  grey: "#808080",
  gray: "#808080",
  orange: "#F59E0B",
  cream: "#FFF5DC",
  tan: "#D2B48C",
  silver: "#C0C0C0",
  fawn: "#E5AA70",
  red: "#EF4444",
  yellow: "#EAB308",
  blue: "#3B82F6",
  chocolate: "#7B3F00",
  beige: "#F5F5DC",
  apricot: "#FBCEB1",
  charcoal: "#36454F",
  sable: "#8D7B68",
};

const getColorHex = (colorVal) => {
  if (!colorVal) return "#d1d5db";
  if (typeof colorVal === "object") {
    if (colorVal.hex) return colorVal.hex;
    if (colorVal.code) return colorVal.code;
    if (colorVal.color) return colorVal.color;
    colorVal = colorVal.name || colorVal.label || "";
  }
  const s = String(colorVal).trim();
  if (s.startsWith("#") || s.startsWith("rgb")) return s;
  return COLOR_HEX_MAP[s.toLowerCase()] || "#F4B400";
};

const PetHeader = ({
  petName,
  petType,
  petBreed,
  petGender,
  petAge,
  petWeight,
  petNeuteredStatus,
  petImage,
  petCoat,
  petTags,
  petColor,
  petDiet,
  petMealsPerDay,
  petSize,
  petDetails,
  medicalConditions,
  vaccinations,
  lastGroomedData,
  daysSinceLastGroom,
  clientName,
  clientPhone,
  onClose,
  onEdit,
  onImageClick,
  formatDate,
  serviceName,
}) => {
  const { profileData } = useProfile();
  const vaccineSetting =
    profileData?.systemSettings?.vaccination_requirement ||
    profileData?.vaccination_requirement;
  const globalEnabled =
    vaccineSetting?.enabled === true ||
    vaccineSetting?.enabled === "true" ||
    vaccineSetting?.enabled === 1 ||
    vaccineSetting === true;

  const normalizeServiceName = (value) =>
    String(value || "").trim().toLowerCase();

  const serviceMatches = (settingName, currentServiceName) => {
    const setting = normalizeServiceName(settingName);
    const current = normalizeServiceName(currentServiceName);
    if (!setting || !current) return false;
    return (
      setting === current ||
      setting.includes(current) ||
      current.includes(setting)
    );
  };

  const isVaccinationRequired = () => {
    if (!globalEnabled) return false;
    if (
      !serviceName ||
      !Array.isArray(vaccineSetting?.services) ||
      vaccineSetting.services.length === 0
    ) {
      return true;
    }
    return vaccineSetting.services.some((s) => {
      const name = s?.name || s?.type || s;
      return serviceMatches(name, serviceName);
    });
  };

  const vaccinationEnabled = isVaccinationRequired();

  const isNeutered =
    petNeuteredStatus === "Neutered" ||
    petNeuteredStatus === "Spayed" ||
    petDetails?.neutered_spayed === true ||
    ["yes", "neutered", "spayed"].includes(
      String(petNeuteredStatus || petDetails?.neutered_spayed || "").toLowerCase()
    );

  const hasVal = (val) => {
    if (val == null) return false;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === "string") return val.trim().length > 0;
    return true;
  };

  // Extract Colors
  const colors = (() => {
    const raw = hasVal(petColor)
      ? petColor
      : petDetails?.color ||
        petDetails?.colors ||
        petDetails?.pet_color ||
        petDetails?.custom_fields?.["pet.color"] ||
        [];
    if (Array.isArray(raw)) {
      return raw
        .map((c) =>
          typeof c === "string" ? c.trim() : c?.name || c?.label || c?.color || ""
        )
        .filter(Boolean);
    }
    if (typeof raw === "string" && raw.trim()) {
      return raw
        .split(/[,;|]+/)
        .map((c) => c.trim())
        .filter(Boolean);
    }
    return [];
  })();

  // Extract Size
  const resolvedSize =
    petSize ||
    petDetails?.size?.size_label ||
    petDetails?.size?.name ||
    (typeof petDetails?.size === "string" ? petDetails.size : "");

  // Extract Coat
  const resolvedCoat =
    petCoat && petCoat !== "—"
      ? petCoat
      : petDetails?.coat ||
        petDetails?.hair_type ||
        petDetails?.breed?.breed_coat ||
        petDetails?.breed_info?.breed_coat ||
        "";

  // Extract Diet
  const dietList = (() => {
    const raw = hasVal(petDiet)
      ? petDiet
      : petDetails?.meal_type ||
        petDetails?.mealType ||
        petDetails?.diet ||
        petDetails?.what_to_feed ||
        petDetails?.custom_fields?.["pet.meal_type"] ||
        [];
    let list = [];
    if (Array.isArray(raw)) {
      list = raw
        .map((d) =>
          typeof d === "string" ? d.trim() : d?.name || d?.label || d?.meal_type || ""
        )
        .filter(Boolean);
    } else if (typeof raw === "string" && raw.trim()) {
      list = raw.split(/[,;|]+/).map((d) => d.trim()).filter(Boolean);
    }
    if (list.length === 0 && Array.isArray(petDetails?.meals) && petDetails.meals.length > 0) {
      const mealNames = petDetails.meals
        .map((m) => m?.meal_type || m?.type || m?.name || "")
        .filter(Boolean);
      if (mealNames.length > 0) list = mealNames;
    }
    return [...new Set(list)];
  })();

  const mealsPerDay =
    petMealsPerDay ||
    petDetails?.meals_per_day ||
    petDetails?.mealsPerDay ||
    null;

  // Extract Medical Conditions
  const medConditionsList = (() => {
    const raw = hasVal(medicalConditions)
      ? medicalConditions
      : petDetails?.medical_conditions ||
        petDetails?.medical_condition ||
        petDetails?.medicalConditions ||
        petDetails?.medicalCondition ||
        petDetails?.medical_notes ||
        petDetails?.custom_fields?.["pet.medical_condition"] ||
        [];
    if (Array.isArray(raw)) {
      return raw
        .map((m) => {
          if (typeof m === "string") return m.trim();
          if (m && typeof m === "object") {
            return m.name || m.condition || m.label || "";
          }
          return "";
        })
        .filter(Boolean);
    }
    if (typeof raw === "string" && raw.trim()) {
      return raw.split(/[,;|]+/).map((m) => m.trim()).filter(Boolean);
    }
    return [];
  })();

  // Extract Vaccines
  const vaccineList = (() => {
    const list = [];
    const fromProps = hasVal(vaccinations) ? vaccinations : [];
    if (Array.isArray(fromProps)) {
      fromProps.forEach((v) => {
        const name = typeof v === "string" ? v.trim() : v?.vaccine || v?.name || "";
        if (name && !list.some((item) => item.toLowerCase() === name.toLowerCase())) {
          list.push(name);
        }
      });
    }
    const recs = petDetails?.vaccination_records || [];
    if (Array.isArray(recs)) {
      recs.forEach((r) => {
        const name = r?.vaccine || r?.vaccination || r?.name || "";
        if (name && !list.some((item) => item.toLowerCase() === name.toLowerCase())) {
          list.push(name);
        }
      });
    }
    const vaxs = petDetails?.vaccinations || petDetails?.vaccinated_for || [];
    if (Array.isArray(vaxs)) {
      vaxs.forEach((v) => {
        const name = typeof v === "string" ? v.trim() : v?.name || v?.vaccine || "";
        if (name && !list.some((item) => item.toLowerCase() === name.toLowerCase())) {
          list.push(name);
        }
      });
    } else if (typeof vaxs === "string" && vaxs.trim()) {
      vaxs.split(/[,;|]+/).forEach((v) => {
        const trimmed = v.trim();
        if (trimmed && !list.some((item) => item.toLowerCase() === trimmed.toLowerCase())) {
          list.push(trimmed);
        }
      });
    }
    return list;
  })();

  // Extract Tags
  const tagList = (() => {
    const raw = hasVal(petTags)
      ? petTags
      : petDetails?.tags ||
        petDetails?.pet_tags ||
        petDetails?.custom_fields?.["pet.tags"] ||
        [];
    if (Array.isArray(raw)) {
      return raw
        .map((t) => (typeof t === "string" ? t.trim() : t?.name || t?.label || ""))
        .filter(Boolean);
    }
    if (typeof raw === "string" && raw.trim()) {
      return raw.split(/[,;|]+/).map((t) => t.trim()).filter(Boolean);
    }
    return [];
  })();

  // Check Rabies
  const hasRabies = (() => {
    const rabiesRegex = /rabies/i;
    if (vaccineList.some((v) => rabiesRegex.test(v))) return true;
    const statusText = (
      petDetails?.vaccination_status ||
      petDetails?.vaccinationStatus ||
      petDetails?.pet_vaccination_status ||
      ""
    ).toLowerCase();
    if (rabiesRegex.test(statusText)) return true;
    return false;
  })();

  return (
    <div className="p-4 border-b bg-gray-50/90">
      {/* Row 1: Photo, Name, Rabies Badge, Subtitle with Color Swatch */}
      <div className="flex items-start gap-3">
        <div
          className={`w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-50 via-white to-blue-50 border border-indigo-100 flex items-center justify-center flex-shrink-0 overflow-hidden shadow-2xs ${
            onImageClick ? "cursor-pointer" : ""
          }`}
          onClick={onImageClick}
        >
          {petImage ? (
            <img src={petImage} alt={petName} className="w-full h-full object-cover" />
          ) : (
            getPetIcon(petType, "w-7 h-7 text-indigo-600")
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-gray-800 capitalize leading-tight">
              {petName}
            </h2>

            {/* Rabies Status Badge */}
            {vaccinationEnabled &&
              (hasRabies ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <FaCheck className="text-[10px] text-emerald-600" />
                  <span>Rabies Vaccinated</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                  <span>Rabies Vaccination is missing.</span>
                </span>
              ))}
          </div>

          {/* Subtitle: Breed • Type • Size • Color with circle swatch */}
          <div className="text-sm text-gray-600 flex items-center gap-1.5 flex-wrap mt-0.5">
            <span>{[petBreed, petType, resolvedSize].filter(Boolean).join(" • ")}</span>

            {colors.length > 0 && (
              <>
                <span className="text-gray-300 font-light select-none">•</span>
                <span className="inline-flex items-center gap-1.5">
                  {colors.map((c, i) => {
                    const hex = getColorHex(c);
                    return (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 font-medium text-gray-700"
                      >
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full border border-black/15 shadow-2xs flex-shrink-0"
                          style={{ backgroundColor: hex }}
                          title={c}
                        />
                        <span>{c}</span>
                        {i < colors.length - 1 && <span className="text-gray-300">,</span>}
                      </span>
                    );
                  })}
                </span>
              </>
            )}
          </div>
        </div>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg shadow-xs transition-colors shrink-0 cursor-pointer"
          >
            <span>Edit</span>
          </button>
        )}
        {onClose && (
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full hover:bg-white flex items-center justify-center transition flex-shrink-0 text-gray-500 hover:text-gray-800 border border-transparent hover:border-gray-200 shadow-2xs cursor-pointer"
            title="Close"
            aria-label="Close"
          >
            <FaTimes className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Row 2: Details grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-x-3 gap-y-1 mt-2 text-sm">
        {petGender && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500 text-xs">Gender:</span>
            <span className="font-medium text-gray-700 text-xs capitalize">{petGender}</span>
          </div>
        )}
        {petAge && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500 text-xs">Age:</span>
            <span className="font-medium text-gray-700 text-xs">{petAge}</span>
          </div>
        )}
        {petWeight && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500 text-xs">Weight:</span>
            <span className="font-medium text-gray-700 text-xs">{petWeight} kg</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <span className="text-gray-500 text-xs">Neutered:</span>
          <span
            className={`font-medium text-xs ${
              isNeutered ? "text-emerald-600" : "text-amber-600"
            }`}
          >
            {isNeutered ? "✓ Neutered/Spayed" : petNeuteredStatus || "Unknown"}
          </span>
        </div>
        {resolvedCoat && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500 text-xs">Coat:</span>
            <span className="font-medium text-gray-700 text-xs">{resolvedCoat}</span>
          </div>
        )}
      </div>

      {/* Row 3: Owner */}
      {(clientName || clientPhone) && (
        <div className="flex items-center gap-4 mt-1.5 pt-1.5 border-t border-gray-200/80 text-sm">
          {clientName && (
            <div className="flex items-center gap-1.5">
              <FaUser className="text-gray-400 text-xs" />
              <span className="text-gray-600 text-xs">Owner:</span>
              <span className="font-medium text-gray-700 text-xs">{clientName}</span>
            </div>
          )}
          {clientPhone && (
            <div className="flex items-center gap-1.5">
              <FaPhone className="text-gray-400 text-xs" />
              <span className="text-gray-600 text-xs">Mobile:</span>
              <span className="font-medium text-gray-700 text-xs">{clientPhone}</span>
            </div>
          )}
        </div>
      )}

      {/* Row 4: Diet, Medical, Vaccines, Tags (Using only icons!) */}
      {(dietList.length > 0 ||
        mealsPerDay ||
        medConditionsList.length > 0 ||
        vaccineList.length > 0 ||
        tagList.length > 0) && (
        <div className="mt-2 space-y-1.5 pt-1.5 border-t border-gray-200/80">
          {/* Diet */}
          {(dietList.length > 0 || mealsPerDay) && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className="inline-flex items-center justify-center text-amber-500 mr-0.5"
                title="Diet"
                aria-label="Diet"
              >
                <FaUtensils className="text-xs" />
              </span>
              {dietList.map((d, i) => {
                const isVeg = /veg(?!.*non)/i.test(d) || /vegetarian/i.test(d);
                const isNonVeg = /non.*veg/i.test(d) || /meat|raw/i.test(d);
                return (
                  <span
                    key={i}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                      isVeg
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : isNonVeg
                          ? "bg-rose-50 text-rose-700 border-rose-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}
                    title={`Diet: ${d}`}
                  >
                    {isVeg ? (
                      <span className="inline-flex items-center justify-center w-3 h-3 border border-emerald-600 rounded-xs bg-white p-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                      </span>
                    ) : isNonVeg ? (
                      <span className="inline-flex items-center justify-center w-3 h-3 border border-rose-600 rounded-xs bg-white p-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-600" />
                      </span>
                    ) : null}
                    <span>{d}</span>
                  </span>
                );
              })}
              {mealsPerDay && (
                <span className="px-2.5 py-0.5 text-xs text-slate-600 bg-slate-100 rounded-full border border-slate-200 font-medium">
                  {mealsPerDay} meals/day
                </span>
              )}
            </div>
          )}

          {/* Medical Conditions */}
          {medConditionsList.length > 0 && (
            <div className="flex items-center flex-wrap gap-1.5">
              <span
                className="inline-flex items-center justify-center text-rose-500 mr-0.5"
                title="Medical Conditions"
                aria-label="Medical Conditions"
              >
                <FaHeartbeat className="text-xs" />
              </span>
              {medConditionsList.map((mc, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200/80"
                >
                  {mc}
                </span>
              ))}
            </div>
          )}

          {/* Vaccines */}
          {vaccineList.length > 0 && (
            <div className="flex items-center flex-wrap gap-1.5">
              <span
                className="inline-flex items-center justify-center text-indigo-500 mr-0.5"
                title="Vaccines"
                aria-label="Vaccines"
              >
                <FaSyringe className="text-xs" />
              </span>
              {vaccineList.map((vax, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200/80"
                >
                  <FaCheck className="text-[9px] text-emerald-600" />
                  <span>{vax}</span>
                </span>
              ))}
            </div>
          )}

          {/* Tags */}
          {tagList.length > 0 && (
            <div className="flex items-center flex-wrap gap-1.5">
              <span
                className="inline-flex items-center justify-center text-sky-500 mr-0.5"
                title="Tags"
                aria-label="Tags"
              >
                <FaTag className="text-xs" />
              </span>
              {tagList.map((tag, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200/80"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

PetHeader.propTypes = {
  petName: PropTypes.string,
  petType: PropTypes.string,
  petBreed: PropTypes.string,
  petGender: PropTypes.string,
  petAge: PropTypes.string,
  petWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  petNeuteredStatus: PropTypes.string,
  petImage: PropTypes.string,
  petCoat: PropTypes.string,
  petTags: PropTypes.array,
  petColor: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  petDiet: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  petMealsPerDay: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  petSize: PropTypes.string,
  petDetails: PropTypes.object,
  medicalConditions: PropTypes.array,
  vaccinations: PropTypes.array,
  lastGroomedData: PropTypes.object,
  daysSinceLastGroom: PropTypes.number,
  clientName: PropTypes.string,
  clientPhone: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onImageClick: PropTypes.func,
  formatDate: PropTypes.func.isRequired,
};

export default PetHeader;